// Schema des Email-Editors: nur was hier steht, kann im Editor existieren – und damit in der Mail ankommen.

import { Extension, Node } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { StarterKit } from '@tiptap/starter-kit';
import { TextStyle, Color, BackgroundColor, FontFamily, FontSize } from '@tiptap/extension-text-style';
import { TextAlign } from '@tiptap/extension-text-align';
import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extensions';
import cleanPastedHtml from './pasteCleaner';
import { addRow } from './templateTable';
import { countLocalImages, extractRtfImages, readAsDataUri, resolveLocalImages } from './clipboardImages';
import { INDENT_STEP_PT, MAX_INDENT, isHouseFont, normalizeBackground, normalizeColor, normalizeFontSize } from './houseStyle';

// Größen auf die Outlook-Liste runden, Standardgröße = kein Attribut
const HouseFontSize = FontSize.extend({
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontSize: {
                    default: null,
                    parseHTML: element => normalizeFontSize(element.style.fontSize),
                    renderHTML: attributes => attributes.fontSize ? {style: `font-size: ${attributes.fontSize}`} : {},
                },
            },
        }];
    },
});

// nur über "Ursprüngliche Formatierung beibehalten"; die Hausschrift selbst ist kein Attribut
const HouseFontFamily = FontFamily.extend({
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontFamily: {
                    default: null,
                    parseHTML: element => {
                        const family = element.style.fontFamily;
                        return family && !isHouseFont(family) ? family.replace(/"/g, "'") : null;
                    },
                    renderHTML: attributes => attributes.fontFamily ? {style: `font-family: ${attributes.fontFamily}`} : {},
                },
            },
        }];
    },
});

const HouseColor = Color.extend({
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                color: {
                    default: null,
                    parseHTML: element => normalizeColor(element.style.color),
                    renderHTML: attributes => attributes.color ? {style: `color: ${attributes.color}`} : {},
                },
            },
        }];
    },
});

const HouseBackgroundColor = BackgroundColor.extend({
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                backgroundColor: {
                    default: null,
                    parseHTML: element => normalizeBackground(element.style.backgroundColor),
                    renderHTML: attributes => attributes.backgroundColor ? {style: `background-color: ${attributes.backgroundColor}`} : {},
                },
            },
        }];
    },
});

// Einzug wie Outlook: Absätze in 36pt-Schritten, in Listen wird die Ebene geändert
export const Indent = Extension.create({
    name: 'indent',
    priority: 50,

    addGlobalAttributes() {
        return [{
            types: ['paragraph'],
            attributes: {
                indent: {
                    default: 0,
                    parseHTML: element => {
                        const margin = element.style.marginLeft;
                        const pt = margin.endsWith('px') ? parseFloat(margin) * 0.75 : parseFloat(margin);
                        return pt > 0 ? Math.min(MAX_INDENT, Math.round(pt / INDENT_STEP_PT)) : 0;
                    },
                    renderHTML: attributes => attributes.indent ? {style: `margin-left: ${attributes.indent * INDENT_STEP_PT}pt`} : {},
                },
            },
        }];
    },

    addCommands() {
        const change = delta => () => ({editor, state, tr, dispatch}) => {
            if (editor.isActive('listItem')) {
                return delta > 0 ? editor.commands.sinkListItem('listItem') : editor.commands.liftListItem('listItem');
            }
            let changed = false;
            state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
                if (node.type.name === 'paragraph') {
                    const indent = Math.max(0, Math.min(MAX_INDENT, (node.attrs.indent || 0) + delta));
                    if (indent !== node.attrs.indent) {
                        tr.setNodeMarkup(pos, undefined, {...node.attrs, indent});
                        changed = true;
                    }
                }
            });
            if (changed && dispatch) {
                dispatch(tr);
            }
            return changed;
        };
        return {
            indent: change(1),
            outdent: change(-1),
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: () => this.editor.isActive('table') ? false : this.editor.commands.indent(),
            'Shift-Tab': () => this.editor.isActive('table') ? false : this.editor.commands.outdent(),
        };
    },
});

// Massenemail-Bausteine (components/emails/bulkTemplateTokens.js): Twig-Quelle Base64 in data-twig, Inhalt ist die
// gerenderte Vorschau. Im Editor ein unteilbares Atom – löschbar, aber nicht editier- oder splitbar.
function twigToken(name, tag, inline) {
    return Node.create({
        name,
        group: inline ? 'inline' : 'block',
        inline,
        atom: true,
        selectable: true,
        draggable: false,

        addAttributes() {
            return {
                twig: {default: null, parseHTML: element => element.getAttribute('data-twig'), renderHTML: () => ({})},
                mode: {default: 'text', parseHTML: element => element.getAttribute('data-twig-mode'), renderHTML: () => ({})},
                id: {default: null, parseHTML: element => element.getAttribute('data-twig-id'), renderHTML: () => ({})},
                html: {default: '', parseHTML: element => element.innerHTML, renderHTML: () => ({})},
            };
        },

        parseHTML() {
            return [{tag: `${tag}[data-twig]`, priority: 100}];
        },

        renderHTML({node}) {
            const element = document.createElement(tag);
            element.className = 'mceNonEditable bulk-twig-token';
            element.setAttribute('contenteditable', 'false');
            element.setAttribute('data-twig', node.attrs.twig);
            element.setAttribute('data-twig-mode', node.attrs.mode);
            if (node.attrs.id) {
                element.setAttribute('data-twig-id', node.attrs.id);
            }
            element.innerHTML = node.attrs.html;
            return element;
        },
    });
}

export const TwigTokenInline = twigToken('twigTokenInline', 'span', true);
export const TwigTokenBlock = twigToken('twigTokenBlock', 'div', false);

// Tabellen – egal ob aus Vorlage (email_bb), eingefügt (Outlook, Word, Excel, Web) oder über "Tabelle einfügen" –
// sind ein geschützter Block: das HTML bleibt Zeichen für Zeichen erhalten, der Text ist direkt bearbeitbar,
// Zeilen/Spalten ändert templateTable.js (Kopie der vorhandenen Zellen). Ein Tabellenmodell im Editor würde
// Breiten, Kopfzeilen und Linien neu berechnen.
export const HTML_BLOCK_ATTRIBUTE = 'data-email-block';

const BLOCK_STYLE = ':host { display: block; } .email-html-block-inner { outline: none; line-height: normal; }'
    // Massenemail-Bausteine in Tabellen (Farben wie in email_editor.scss)
    + ' .bulk-twig-token { background-color: #eef2f6; outline: 1px dashed #b9c6d2; border-radius: 3px; } span.bulk-twig-token { padding: 0 2px; }';

export const HtmlBlock = Node.create({
    name: 'htmlBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: false,

    addAttributes() {
        return {
            html: {default: '', parseHTML: element => {
                const copy = element.cloneNode(true);
                copy.removeAttribute(HTML_BLOCK_ATTRIBUTE);
                return copy.outerHTML;
            }, renderHTML: () => ({})},
        };
    },

    parseHTML() {
        return [
            {tag: `[${HTML_BLOCK_ATTRIBUTE}]`, priority: 95},
            {tag: 'table', priority: 90},
        ];
    },

    renderHTML({node}) {
        const container = document.createElement('div');
        container.innerHTML = node.attrs.html;
        const element = container.firstElementChild || container;
        element.setAttribute(HTML_BLOCK_ATTRIBUTE, '1');
        return element;
    },

    // Darstellung im Shadow DOM: Seiten-CSS (tiptap.scss, modal.scss, Bulma) erreicht die Tabelle nicht –
    // sie sieht aus wie im Mailprogramm (früher: TinyMCE-iframe). Schrift erbt vom Editor wie vom <td> der Mail.
    addKeyboardShortcuts() {
        const enter = direction => ({editor}) => {
            const {state, view} = editor;
            const {$from, empty} = state.selection;
            if (!empty || $from.depth !== 1 || !view.endOfTextblock(direction > 0 ? 'down' : 'up')) {
                return false;
            }
            const index = $from.index(0) + direction;
            const neighbour = index >= 0 ? state.doc.maybeChild(index) : null;
            if (!neighbour || neighbour.type.name !== this.name) {
                return false;
            }
            const pos = direction > 0 ? $from.after(1) : $from.before(1) - neighbour.nodeSize;
            const dom = view.nodeDOM(pos);
            if (!dom || !dom.emailBlockEnter) {
                return false;
            }
            dom.emailBlockEnter(direction > 0 ? 'start' : 'end');
            return true;
        };
        return {
            ArrowDown: enter(1),
            ArrowUp: enter(-1),
        };
    },

    addNodeView() {
        return ({node, editor, getPos}) => {
            let current = node;
            const dom = document.createElement('div');
            dom.className = 'email-html-block';
            dom.setAttribute('contenteditable', 'false');
            const shadow = dom.attachShadow({mode: 'open'});
            const style = document.createElement('style');
            style.textContent = BLOCK_STYLE;
            const inner = document.createElement('div');
            inner.className = 'email-html-block-inner';
            inner.setAttribute('contenteditable', editor.isEditable ? 'true' : 'false');
            inner.innerHTML = current.attrs.html;
            shadow.appendChild(style);
            shadow.appendChild(inner);

            const sync = () => {
                const pos = typeof getPos === 'function' ? getPos() : null;
                const html = inner.innerHTML;
                if (typeof pos !== 'number' || html === current.attrs.html) {
                    return;
                }
                editor.view.dispatch(editor.view.state.tr.setNodeMarkup(pos, undefined, {...current.attrs, html}));
            };
            inner.addEventListener('input', sync);

            // Pfeiltasten wie in Outlook: aus der ersten/letzten Zeile heraus in den Text davor/danach
            const leave = direction => {
                const pos = typeof getPos === 'function' ? getPos() : null;
                if (typeof pos !== 'number') {
                    return;
                }
                const {state} = editor.view;
                const $pos = state.doc.resolve(direction > 0 ? pos + current.nodeSize : pos);
                const selection = Selection.near($pos, direction);
                editor.view.focus();
                editor.view.dispatch(state.tr.setSelection(selection).scrollIntoView());
            };
            const caret = () => {
                const selection = shadow.getSelection ? shadow.getSelection() : window.getSelection();
                return selection && selection.rangeCount ? selection.getRangeAt(0) : null;
            };
            // Tab / Umschalt+Tab: nächste bzw. vorige Zelle (wie Outlook); Tab in der letzten Zelle hängt eine Zeile an
            inner.addEventListener('keydown', event => {
                if (event.key !== 'Tab') {
                    return;
                }
                const range = caret();
                const node = range && range.startContainer;
                const element = node && (node.nodeType === 1 ? node : node.parentElement);
                const cell = element && element.closest('td, th');
                if (!cell) {
                    return;
                }
                event.preventDefault();
                const cells = Array.from(cell.closest('table').querySelectorAll('td, th'))
                    .filter(other => other.closest('table') === cell.closest('table'));
                let target = cells[cells.indexOf(cell) + (event.shiftKey ? -1 : 1)];
                if (!target && !event.shiftKey) {
                    target = addRow(cell.parentElement, 'after').cells[0];
                    inner.dispatchEvent(new Event('input'));
                }
                if (target) {
                    const selection = window.getSelection();
                    const next = document.createRange();
                    next.selectNodeContents(target);
                    selection.removeAllRanges();
                    selection.addRange(next);
                }
            });

            inner.addEventListener('keydown', event => {
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                    return;
                }
                const range = caret();
                const node = range && range.startContainer;
                const element = node && (node.nodeType === 1 ? node : node.parentElement);
                const cell = element && element.closest('td, th');
                const table = cell && cell.closest('table');
                if (!cell || !table) {
                    return;
                }
                const down = event.key === 'ArrowDown';
                const row = cell.parentElement;
                const rows = Array.from(inner.querySelectorAll('tr')).filter(tr => !tr.parentElement.closest('td'));
                if (row !== (down ? rows[rows.length - 1] : rows[0])) {
                    return;
                }
                // nur auf der letzten bzw. ersten Textzeile der Zelle verlassen
                const caretRect = range.getClientRects()[0] || range.getBoundingClientRect();
                const cellRect = cell.getBoundingClientRect();
                if (caretRect && caretRect.height && (down ? cellRect.bottom - caretRect.bottom : caretRect.top - cellRect.top) > caretRect.height * 0.8) {
                    return;
                }
                event.preventDefault();
                leave(down ? 1 : -1);
            });

            // von außen per Pfeiltaste hinein: Cursor an den Anfang der ersten bzw. ans Ende der letzten Zelle
            dom.emailBlockEnter = where => {
                const walker = document.createTreeWalker(inner, NodeFilter.SHOW_TEXT);
                const texts = [];
                while (walker.nextNode()) {
                    if (walker.currentNode.textContent.trim()) {
                        texts.push(walker.currentNode);
                    }
                }
                inner.focus();
                const target = where === 'end' ? texts[texts.length - 1] : texts[0];
                const range = document.createRange();
                if (target) {
                    range.setStart(target, where === 'end' ? target.textContent.length : 0);
                } else {
                    range.selectNodeContents(inner.querySelector('td, th') || inner);
                }
                range.collapse(true);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
            };

            // in die Baustein-Tabelle nur Text einfügen, sonst landet dort wieder fremde Formatierung
            inner.addEventListener('paste', event => {
                event.preventDefault();
                event.stopPropagation();
                document.execCommand('insertText', false, (event.clipboardData && event.clipboardData.getData('text/plain')) || '');
            });

            return {
                dom,
                update(updated) {
                    if (updated.type !== current.type) {
                        return false;
                    }
                    current = updated;
                    if (updated.attrs.html !== inner.innerHTML) {
                        inner.innerHTML = updated.attrs.html;
                    }
                    return true;
                },
                // Ereignisse aus dem Shadow DOM kommen mit dem Block selbst als target an
                stopEvent: event => event.target === dom,
                ignoreMutation: () => true,
            };
        };
    },
});

export const emailPasteKey = new PluginKey('emailPaste');

// Einfügen: Word/Outlook-Bilder aus dem RTF-Teil einsetzen, auf Hausstil reduzieren (pasteCleaner) und den
// eingefügten Bereich merken – die Komponente bietet daran die Einfügeoptionen an (wie Word/Outlook).
export const EmailPaste = Extension.create({
    name: 'emailPaste',

    addOptions() {
        return {
            onPasted: null,
        };
    },

    addProseMirrorPlugins() {
        const extension = this;
        let pending = null;

        return [new Plugin({
            key: emailPasteKey,

            state: {
                init: () => null,
                apply(tr, value, oldState, newState) {
                    if (tr.getMeta('uiEvent') === 'paste' && pending) {
                        return {from: tr.mapping.map(oldState.selection.from, -1), to: newState.selection.to, clipboard: pending};
                    }
                    return null;
                },
            },

            view: () => ({
                update(view, previousState) {
                    const pasted = emailPasteKey.getState(view.state);
                    if (pasted && pasted !== emailPasteKey.getState(previousState) && extension.options.onPasted) {
                        extension.options.onPasted(pasted);
                        pending = null;
                    }
                },
            }),

            props: {
                handleDOMEvents: {
                    paste(view, event) {
                        const data = event.clipboardData;
                        if (!data) {
                            return false;
                        }
                        const html = data.getData('text/html');
                        const text = data.getData('text/plain');
                        const imageFiles = Array.from(data.files || []).filter(file => /^image\/(png|jpe?g|gif)$/.test(file.type));

                        // nur ein Bild (Screenshot, kopierte Grafik): direkt einfügen, Upload macht die Komponente
                        if (!html && !text && imageFiles.length) {
                            event.preventDefault();
                            Promise.all(imageFiles.map(readAsDataUri)).then(sources => {
                                extension.editor.chain().focus().insertContent(sources.map(src => ({type: 'image', attrs: {src}}))).run();
                            });
                            return true;
                        }

                        pending = {html, text, resolvedHtml: html};
                        // Fehlersuche bei seltsamem Einfügen: in der Browser-Konsole copy(__emailEditorLastPaste.html)
                        window.__emailEditorLastPaste = {html, text, rtf: data.getData('text/rtf')};
                        if (html && countLocalImages(html)) {
                            const rtfImages = extractRtfImages(data.getData('text/rtf'));
                            if (rtfImages.some(Boolean)) {
                                pending.resolvedHtml = resolveLocalImages(html, rtfImages);
                            } else if (imageFiles.length === countLocalImages(html)) {
                                // Browser liefert die Bilder als Dateien mit (z.B. eine einzelne Grafik aus Word)
                                event.preventDefault();
                                const current = pending;
                                Promise.all(imageFiles.map(readAsDataUri)).then(sources => {
                                    current.resolvedHtml = resolveLocalImages(html, sources);
                                    pending = current;
                                    view.pasteHTML(current.resolvedHtml);
                                });
                                return true;
                            }
                        }
                        return false;
                    },
                },

                transformPastedHTML: html => cleanPastedHtml(pending && pending.html === html ? pending.resolvedHtml : html),
            },
        })];
    },
});

export default function emailExtensions({placeholder = '', onPasted = null} = {}) {
    return [
        StarterKit.configure({
            heading: false,
            blockquote: false,
            code: false,
            codeBlock: false,
            horizontalRule: false,
            link: {
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
                HTMLAttributes: {target: null, rel: null},
            },
        }),
        TextStyle,
        HouseFontFamily,
        HouseFontSize,
        HouseColor,
        HouseBackgroundColor,
        TextAlign.configure({types: ['paragraph'], alignments: ['left', 'center', 'right', 'justify']}),
        Indent,
        HtmlBlock,
        Image.configure({inline: true, allowBase64: true}),
        // eigene Klassennamen: das globale .is-empty (pages/common.scss: padding 20px) würde leere Zeilen aufblähen
        Placeholder.configure({placeholder, emptyNodeClass: 'email-editor-empty-line', emptyEditorClass: 'email-editor-empty'}),
        TwigTokenInline,
        TwigTokenBlock,
        EmailPaste.configure({onPasted}),
    ];
}
